import { reportEvent } from '@/lib/monitoring'
import { ServiceError } from '../errors'
import { pmsUrl, postJson } from '../http'
import { deriveKeyStatus, entryGuideItem, openCommand } from './key'
import type { Stay } from '../reserva/types'
import type { AccesosService, Directions, OpenResult } from './types'

/**
 * Portado de src/components/swipeButton/SwipeButton.tsx y src/api/usedomoticcommand/ de la app actual:
 * E17 `usedomoticcommand` sin cabecera de auth, hasta 2 reintentos con 3 s de espera, y telemetría a
 * `{VITE_DOOR_STATS_API_HOST}/door/attempt` (E18) que se ignora si falla.
 */
const RETRIES = 2
const RETRY_DELAY_MS = 3000

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

function reportAttempt(body: unknown): void {
  const host = import.meta.env.VITE_DOOR_STATS_API_HOST
  if (!host || typeof fetch !== 'function') return
  fetch(`${host}/door/attempt`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true }).catch(() => undefined)
}

/**
 * Con la API actual solo hay dirección, coordenadas y teléfono (E1). Transporte, aparcamiento, nota de la
 * dirección y fuera de horario no existen: vacíos, nunca datos de ejemplo (D36).
 */
export function directionsFromStay(stay: Pick<Stay, 'unit' | 'contact'>): Directions {
  return {
    street: stay.unit.street || stay.unit.address,
    locality: stay.unit.street ? stay.unit.locality : null,
    addressNote: null,
    lat: stay.unit.lat,
    lng: stay.unit.lng,
    transport: [],
    parking: null,
    afterHoursText: null,
    emergencyPhone: stay.contact.emergencyPhone ?? stay.contact.supportPhone,
  }
}

export const accesosReal: AccesosService = {
  getKey(stay) {
    return Promise.resolve(deriveKeyStatus(stay))
  },

  async open(stay, accessId, lang, options = {}) {
    const dispositive = stay.dispositives.find((d) => d.id === accessId)
    const command = dispositive ? openCommand(dispositive) : null
    if (!dispositive || !command) return { status: 'error', message: 'no_command' }
    const attempt = (body: unknown) =>
      reportAttempt({
        // Como SwipeButton.tsx: roomId, roomName y roomAddress de E1.
        room: { id: stay.unit.roomId, name: stay.unit.name, address: stay.unit.street },
        dispositive: { id: dispositive.id, name: dispositive.name },
        type: dispositive.type,
        guestCode: stay.guestCode,
        identifier: stay.reference,
        ...(body as object),
      })
    let lastError: string | null = null
    // Para Sentry solo el tipo de fallo, nunca el texto del PMS.
    let lastErrorKind: 'not_ok' | 'server' = 'server'
    for (let i = 0; i <= RETRIES; i++) {
      try {
        const res = await postJson<{ ok: boolean; error?: string }>(
          // SwipeButton.tsx usa solo `command.serverPath`; si el PMS no lo da, v2 usa el de la reserva en lugar de llamar
          // a `https:///…` (fase 4, A13).
          pmsUrl(command.serverPath || stay.serverPath, 'usedomoticcommand'),
          { reserveId: stay.id, commandId: command.id, userLang: lang },
          { auth: 'none', ...(options.signal ? { signal: options.signal } : {}) },
        )
        if (res.ok) {
          attempt({ success: true })
          return { status: 'opened' }
        }
        lastError = res.error ?? 'not_ok'
        lastErrorKind = 'not_ok'
      } catch (e) {
        // Sin red no se reintenta (SwipeButton.tsx reintenta ante cualquier error): 6 s más de espera en la puerta no
        // abrirían nada y el huésped ve antes el error con el teléfono (fase 4, A11; D43).
        if (e instanceof ServiceError && e.code === 'offline') {
          attempt({ success: false, data: { error: 'offline' } })
          return { status: 'error', message: 'offline' }
        }
        lastError = e instanceof Error ? e.message : String(e)
        lastErrorKind = 'server'
      }
      if (i < RETRIES) await wait(options.retryDelayMs ?? RETRY_DELAY_MS)
    }
    attempt({ success: false, data: { error: lastError } })
    reportEvent('door_open_failed', { accessId, code: lastErrorKind, attempts: RETRIES + 1 }, 'error')
    const result: OpenResult = { status: 'error', message: lastError }
    return result
  },

  // Sin cerradura conectada: el ítem de la guía sobre cómo entrar (D40). Pasos con foto y recepción no existen (D36).
  getInstructions(stay) {
    return Promise.resolve({
      reception: null,
      steps: [],
      guideItem: entryGuideItem(stay),
      emergencyPhone: stay.contact.emergencyPhone ?? stay.contact.supportPhone,
    })
  },

  getDirections(stay) {
    return Promise.resolve(directionsFromStay(stay))
  },
}
